import { useEffect, useMemo, useState } from 'react'
import { ExternalLink } from 'lucide-react'
import { fetchTopArtists } from '../lib/api'
import type { SpotifyArtist } from '../lib/api'

const STORAGE_KEY = 'ah:artist-threshold'

export default function ArtistDiscoveryTile() {
  const [artists, setArtists] = useState<SpotifyArtist[]>([])
  const [threshold, setThreshold] = useState<number>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      return stored != null ? Number(stored) : 60
    } catch {
      return 60
    }
  })

  useEffect(() => {
    fetchTopArtists().then(setArtists).catch(() => {})
  }, [])

  // null popularity means Spotify didn't return the field — always include those artists
  const popularityUnavailable = artists.length > 0 && artists.every(a => a.popularity === null)

  const filtered = useMemo(
    () =>
      popularityUnavailable
        ? artists
        : artists.filter(a => a.popularity === null || a.popularity < threshold),
    [artists, threshold, popularityUnavailable],
  )

  const handleThreshold = (val: number) => {
    setThreshold(val)
    try {
      localStorage.setItem(STORAGE_KEY, String(val))
    } catch {
      // localStorage unavailable (private browsing, etc.) — silently ignore
    }
  }

  return (
    <div className="life-card rounded-xl p-6">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-lg font-heading font-medium text-amber-100">Artist Discovery</h3>
        <span className="text-xs text-amber-200/50 uppercase tracking-wider">Lately into</span>
      </div>

      {!popularityUnavailable && (
        <div className="mb-5 mt-3">
          <label
            htmlFor="artist-threshold"
            className="text-xs text-amber-200/60 mb-2 block"
          >
            Popularity &lt; {threshold}
          </label>
          <input
            id="artist-threshold"
            type="range"
            min={0}
            max={100}
            step={5}
            value={threshold}
            onChange={e => handleThreshold(Number(e.target.value))}
            className="w-full h-1.5 rounded-full bg-amber-400/20 accent-amber-400 cursor-pointer"
          />
        </div>
      )}

      <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
        {filtered.map(a => (
          <div key={a.id} className="flex items-center gap-3 group">
            {a.image ? (
              <img
                src={a.image}
                alt={a.name}
                className="w-10 h-10 rounded-full object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-amber-400/10 flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-amber-50 text-sm font-medium truncate">{a.name}</p>
              {a.genres[0] && (
                <span className="inline-block text-xs bg-amber-400/10 text-amber-300/70 px-2 py-0.5 rounded mt-0.5 truncate max-w-full">
                  {a.genres[0]}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {a.popularity !== null && (
                <span className="text-xs text-amber-200/40 tabular-nums">{a.popularity}</span>
              )}
              {a.spotifyUrl && (
                <a
                  href={a.spotifyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-amber-300/40 hover:text-amber-300 transition-colors opacity-0 group-hover:opacity-100"
                  aria-label={`Open ${a.name} in Spotify`}
                >
                  <ExternalLink size={13} />
                </a>
              )}
            </div>
          </div>
        ))}

        {filtered.length === 0 && (
          <p className="text-amber-200/40 text-sm">
            {artists.length === 0
              ? 'No data yet'
              : 'No artists match — try raising the threshold'}
          </p>
        )}
      </div>
    </div>
  )
}
